import {
  Card,
  CardBody,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  NavItem,
  NavLink,
  Nav,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Input,
  Button,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useMemo, React, useState, useEffect, useContext } from "react";
import { NavLink as DomNavLink, useHistory } from "react-router-dom";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { MainContext } from "context/MainContext.js";

import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import { FaCheckCircle, FaSearch, FaSpinner, FaQuestion } from "react-icons/fa";
import { BsFillXCircleFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import Switch from "react-bootstrap-switch";
import LoadingOverlay from "react-loading-overlay";
import { ToastContainer, toast } from "react-toastify";

const GamConfigurationList = () => {
  const history = useHistory();
  const [alert, setAlert] = useState(null);
  const { t } = useTranslation();
  const privileges = useContext(MainContext);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [horizontalTabs, setHorizontalTabs] = useState("gam");
  const [showLoading, setShowLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState([]);

  useEffect(() => {
    if (
      !Utilities.hasAccess(privileges["automations.gam.configuration.display"])
    ) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const redirectToEditPage = (serialNumber) => {
    const location = {
      pathname: "/admin/automations/configuration/edit",
      state: {
        value: serialNumber ? "serialNumber||" + serialNumber : "",
        mode: serialNumber ? "edit" : "create",
      },
    };
    history.push(location);
  };

  const deleteConfirm = (serialNumber) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure you want to delete this configuration ?"
        onConfirm={() => {
          deleteConfiguration(serialNumber);
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="danger"
        cancelBtnBsStyle="info"
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
        btnSize=""
      >
        You will not be able to recover the configuration '{serialNumber}'
      </ReactBSAlert>,
    );
  };

  const deleteConfiguration = (serialNumber) => {
    const start = Date.now();

    // Call the API
    fetch("/api/v1/automation/gam/configuration/delete", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        serialNumber: serialNumber,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        if (actualData.status == "200") {
          if (actualData.error_code) {
            errorDelete(actualData.error_code[0]);
          } else {
            successDelete();
            setForceRefreshCount((prevValue) => ++prevValue);
          }
        } else {
          errorDelete(actualData.error);
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const successDelete = () => {
    setAlert(
      <ReactBSAlert
        success
        style={{ display: "block", marginTop: "-100px" }}
        title="Deleted!"
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        btnSize=""
      >
        The configuration has been deleted.
      </ReactBSAlert>,
    );
  };

  const errorDelete = (errorMessage) => {
    setAlert(
      <ReactBSAlert
        error
        style={{ display: "block", marginTop: "-100px" }}
        title="Error!"
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        btnSize=""
      >
        {errorMessage}
      </ReactBSAlert>,
    );
  };

  const handleSwitchChange = (e) => {
    const { props, state } = e;

    setValues({
      ...values,
      [props.name]: state.value,
    });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "status",
        sortBy: "status",
        flex: "0 1 5%",
        Cell: ({ row, value }) => {
          let icon = (
            <FaSearch
              className="tableIconFontSize"
              style={{ color: "darkgrey" }}
            />
          );
          if (value === "CONFIGURED_DIVERGE") {
            icon = <FaCheckCircle className="text-warning tableIconFontSize" />;
          } else if (value === "CONFIGURED") {
            icon = <FaCheckCircle className="text-info tableIconFontSize" />;
          } else if (value === "ERROR") {
            icon = (
              <BsFillXCircleFill className="text-danger tableIconFontSize" />
            );
          } else if (value === "WAITING") {
            icon = <FaSpinner className="text-primary tableIconFontSize" />;
          } else if (value === "CONFIGURED_OFFLINE") {
            icon = <FaQuestion className="text-danger tableIconFontSize" />;
          }

          const tooltipId = `vmElement-${row.index}`;

          return (
            <>
              <a id={tooltipId} className="vmElement">
                {icon}
              </a>
              <ReactTooltip
                anchorSelect={`#${tooltipId}`}
                place="top"
                style={{ textAlign: "left", zIndex: "100" }}
              >
                <div>{t("table.row.gam.provisioning.status." + value)}</div>
                {row.original.errorMsg ? (
                  <div style={{ marginTop: "10px" }}>
                    {row.original.errorMsg}
                  </div>
                ) : (
                  ""
                )}
              </ReactTooltip>
            </>
          );
        },
      },
      {
        Header: "Serial Number",
        accessor: "serialNumber",
        sortBy: "serialNumber",
        flex: "1 1 0",
        Cell: (props) => {
          return (
            <a
              onClick={() =>
                redirectToEditPage(props.row.original.serialNumber)
              }
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
      },
      {
        Header: "System Name",
        accessor: "systemName",
        sortBy: "systemName",
        flex: "1 1 0",
      },
      {
        Header: "Allow Manual Changes",
        accessor: "allowManualChanges",
        sortBy: "allowManualChange",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "",
        accessor: "actions",
        Cell: (props) => {
          return (
            <>
              {Utilities.hasAccess(
                privileges["automations.gam.configuration.edit"],
              ) ? (
                <UncontrolledDropdown direction="down">
                  <DropdownToggle
                    caret
                    color="info"
                    style={{
                      padding: "6px 14px 6px 14px",
                    }}
                  >
                    Actions
                  </DropdownToggle>
                  <DropdownMenu container={"body"}>
                    <DropdownItem
                      onClick={() =>
                        redirectToEditPage(props.row.original.serialNumber)
                      }
                    >
                      Edit Configuration
                    </DropdownItem>

                    <>
                      <DropdownItem divider />
                      <DropdownItem
                        onClick={() =>
                          deleteConfirm(props.row.original.serialNumber)
                        }
                      >
                        Delete Configuration
                      </DropdownItem>
                    </>
                  </DropdownMenu>
                </UncontrolledDropdown>
              ) : (
                ""
              )}
            </>
          );
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  return (
    <>
      <ToastContainer />

      <div className="content">
        {errorMsg.length > 0 ? (
          <div
            style={{
              backgroundColor: "#FFC0A4",
              border: "1px solid #ef8157",
              color: "#66615b",
              paddingTop: "15px",
              marginBottom: "15px",
            }}
          >
            <ul>
              {errorMsg.map((d, index) => (
                <li key={index}>{d}</li>
              ))}
            </ul>
          </div>
        ) : (
          ""
        )}
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Loading"
          className={showLoading ? "loading" : ""}
        />
        {alert}
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <DomNavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </DomNavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Automations</BreadcrumbItem>
                  <BreadcrumbItem>GAM Provisioning</BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <ReactTable
                  query="/api/v1/automation/gam/configuration/list"
                  columns={columns}
                  title="AutomaticGamConfigurations"
                  className="-striped -highlight primary-pagination"
                  forceRefresh={forceRefreshCount}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default GamConfigurationList;
